import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import styles from './FullName.module.css'
import { FormTitle } from '../../../Common/FormTitle'
import { NextButton } from '../../../Common/CtaButton/NextButton'
import { FormField } from '../../../Common/FormField'
import { checkFormValidity, validateForm } from './validate'
import {
    NameFormErrors,
    NameFormFieldName,
    NameFormTouched,
    RenderFormFieldProps,
} from './types'
import { useAbTestGtm } from '../../../../hooks/useAbTestGtm'
import { StepContext } from '../../StepContainer'

const HIRAGANA_TO_KATAKANA_OFFSET = 0x60

const hiraganaToKatakana = (str: string): string => {
    return str.replace(/[\u3040-\u309F]/g, ch =>
        String.fromCharCode(ch.charCodeAt(0) + HIRAGANA_TO_KATAKANA_OFFSET)
    )
}

const isHiraganaOnly = (str: string): boolean => {
    if (!str.trim()) return false
    return /^[\u3040-\u309F]+$/.test(str)
}

export const FullName = () => {
    const { dataClickLabel, dataViewLabel } = useAbTestGtm()
    const { formState } = useContext(StepContext)
    const [lastName, setLastName] = useState(formState.lastName || '')
    const [firstName, setFirstName] = useState(formState.firstName || '')
    const [lastNameKana, setLastNameKana] = useState(
        formState.lastNameKana || ''
    )
    const [firstNameKana, setFirstNameKana] = useState(
        formState.firstNameKana || ''
    )

    // DEV-3482: AG会員の転記があるときは姓・名を転記値で固定する（Figma 2970）。
    // 全ステップが常時マウントされていて、この画面より後に転記が届くため、
    // useStateの初期値だけでは追いつかない。
    // 転記が取り消されたとき（メールアドレスを変えたとき）も、前の人の氏名を
    // 残さないよう同じ経路で空に戻す。
    // カナも一緒に追従させる。姓名だけ差し替えると「姓名はAG会員・カナは前に入れた
    // 別の氏名」という組み合わせが残り、バリデーションも通ってしまう
    const isPrefilled = !!formState.agPrefillAppliedEmail
    // 転記値が空（AG側の汚れデータ）のときは編集可のままにする。空かつ編集不可だと
    // checkFormValidity が姓名の非空を求めるため「次へ」が永久に無効になり、
    // メールアドレスを変える以外に抜け道が無くなる
    const isLastNameLocked = isPrefilled && !!formState.lastName
    const isFirstNameLocked = isPrefilled && !!formState.firstName

    useEffect(() => {
        setLastName(formState.lastName || '')
        setFirstName(formState.firstName || '')
        setLastNameKana(formState.lastNameKana || '')
        setFirstNameKana(formState.firstNameKana || '')
        // 空に戻ったときは手入力フラグも降ろす（handleLastNameChangeと同じ扱い）。
        // 残すとカナを手入力した人は、転記を取り消したあともひらがなからの自動変換が戻らない
        if (!formState.lastNameKana) {
            isLastNameKanaManuallyEditedRef.current = false
        }
        if (!formState.firstNameKana) {
            isFirstNameKanaManuallyEditedRef.current = false
        }
    }, [
        isPrefilled,
        formState.lastName,
        formState.firstName,
        formState.lastNameKana,
        formState.firstNameKana,
    ])

    const [touched, setTouched] = useState<NameFormTouched>({
        lastName: false,
        firstName: false,
        lastNameKana: false,
        firstNameKana: false,
    })

    const [errors, setErrors] = useState<NameFormErrors>({
        lastName: '',
        firstName: '',
        lastNameKana: '',
        firstNameKana: '',
    })

    const [isFormValid, setIsFormValid] = useState(false)

    const isComposingLastNameRef = useRef(false)
    const isComposingFirstNameRef = useRef(false)
    const isLastNameKanaManuallyEditedRef = useRef(false)
    const isFirstNameKanaManuallyEditedRef = useRef(false)

    const handleValidation = useCallback(() => {
        const formValues = {
            lastName,
            firstName,
            lastNameKana,
            firstNameKana,
        }

        const newErrors = validateForm(formValues, touched)
        setErrors(newErrors)
        setIsFormValid(checkFormValidity(formValues))
    }, [lastName, firstName, lastNameKana, firstNameKana, touched])

    const handleBlur = (fieldName: NameFormFieldName) => {
        setTouched(prev => ({
            ...prev,
            [fieldName]: true,
        }))
    }

    useEffect(() => {
        handleValidation()
    }, [handleValidation])

    const renderFormField = ({
        fieldName,
        fieldValue,
        setFieldValue,
        label,
        placeholder,
    }: RenderFormFieldProps) => (
        <div>
            <FormField
                label={label}
                placeholder={placeholder}
                value={fieldValue}
                onChange={setFieldValue}
                onBlur={() => handleBlur(fieldName)}
            />
            {errors[fieldName] && (
                <p className={styles.errorMessages}>{errors[fieldName]}</p>
            )}
        </div>
    )

    const handleLastNameChange = useCallback((value: string) => {
        setLastName(value)
        if (value === '') {
            isLastNameKanaManuallyEditedRef.current = false
        }
        if (
            !isComposingLastNameRef.current &&
            !isLastNameKanaManuallyEditedRef.current &&
            isHiraganaOnly(value)
        ) {
            setLastNameKana(hiraganaToKatakana(value))
        }
    }, [])
    const handleFirstNameChange = useCallback((value: string) => {
        setFirstName(value)
        if (value === '') {
            isFirstNameKanaManuallyEditedRef.current = false
        }
        if (
            !isComposingFirstNameRef.current &&
            !isFirstNameKanaManuallyEditedRef.current &&
            isHiraganaOnly(value)
        ) {
            setFirstNameKana(hiraganaToKatakana(value))
        }
    }, [])

    const setLastNameKanaFromUser = useCallback((value: string) => {
        isLastNameKanaManuallyEditedRef.current = true
        setLastNameKana(value)
    }, [])
    const setFirstNameKanaFromUser = useCallback((value: string) => {
        isFirstNameKanaManuallyEditedRef.current = true
        setFirstNameKana(value)
    }, [])

    return (
        <div className={styles.wrapper}>
            <div className={styles.selectionWrapper}>
                <div className={styles.monthbox}>
                    <FormTitle
                        formTitleText="あなたの名前は？"
                        dataViewLabel={dataViewLabel('EF_StepForm_氏名')}
                        dataClickLabel={dataClickLabel('EF_StepForm_氏名-戻る')}
                    />
                </div>

                <div className={styles.nameField}>
                    <div>
                        <FormField
                            label="姓"
                            placeholder="山田"
                            value={lastName}
                            disabled={isLastNameLocked}
                            onChange={handleLastNameChange}
                            onBlur={() => handleBlur('lastName')}
                            onCompositionStart={() => {
                                isComposingLastNameRef.current = true
                            }}
                            onCompositionEnd={() => {
                                isComposingLastNameRef.current = false
                            }}
                            onCompositionUpdate={e => {
                                if (
                                    !isLastNameKanaManuallyEditedRef.current &&
                                    isHiraganaOnly(e.data)
                                ) {
                                    setLastNameKana(hiraganaToKatakana(e.data))
                                }
                            }}
                        />
                        {errors.lastName && (
                            <p className={styles.errorMessages}>
                                {errors.lastName}
                            </p>
                        )}
                    </div>
                    <div>
                        <FormField
                            label="名"
                            placeholder="太郎"
                            value={firstName}
                            disabled={isFirstNameLocked}
                            onChange={handleFirstNameChange}
                            onBlur={() => handleBlur('firstName')}
                            onCompositionStart={() => {
                                isComposingFirstNameRef.current = true
                            }}
                            onCompositionEnd={() => {
                                isComposingFirstNameRef.current = false
                            }}
                            onCompositionUpdate={e => {
                                if (
                                    !isFirstNameKanaManuallyEditedRef.current &&
                                    isHiraganaOnly(e.data)
                                ) {
                                    setFirstNameKana(hiraganaToKatakana(e.data))
                                }
                            }}
                        />
                        {errors.firstName && (
                            <p className={styles.errorMessages}>
                                {errors.firstName}
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.furiganaField}>
                    {renderFormField({
                        fieldName: 'lastNameKana',
                        fieldValue: lastNameKana,
                        setFieldValue: setLastNameKanaFromUser,
                        label: 'セイ',
                        placeholder: 'ヤマダ',
                    })}
                    {renderFormField({
                        fieldName: 'firstNameKana',
                        fieldValue: firstNameKana,
                        setFieldValue: setFirstNameKanaFromUser,
                        label: 'メイ',
                        placeholder: 'タロウ',
                    })}
                </div>

                <NextButton
                    formKey={[
                        'firstName',
                        'lastName',
                        'firstNameKana',
                        'lastNameKana',
                    ]}
                    formValue={[
                        firstName,
                        lastName,
                        firstNameKana,
                        lastNameKana,
                    ]}
                    displayFormKey={['name', 'nameKana']}
                    displayFormValue={[
                        `${lastName} ${firstName}`,
                        `${lastNameKana} ${firstNameKana}`,
                    ]}
                    isDisabled={!isFormValid}
                    dataClickLabel={dataClickLabel('EF_StepForm_氏名-次へ')}
                />
            </div>
        </div>
    )
}
